import { cloneDeep } from 'lodash-es'
import type { CodeWithCategory, CodeWithCategoryAndChildren } from '@/api/generated/v2'


export const allCodesPath = '000000000000000000' // Все коды, абстрактный узел

export const codeTypes = {
  regional: ['082'], // Административно-территориальные образования, ОКАТО
  iptc: ['900'], // IPTC
  publication: ['902'], // Направления публикаций
  publicationTechnical: ['9029'], // Технологические направления (публикаций)
  periodical: ['907'], // Периодические издания
  moex: ['905'], // Коды РТС
  spb: ['910'], // Коды СПБ
  feed: [
    '950', // Информационная продукция Интерфакс
    '951', // Специальная информационная продукция
  ],
} as const

export const shownCodeCatsOrder = {
  '950000000000000000': 0, // Информационная продукция ЗАО "Интерфакс"
  '951000000000000000': 1, // Специальная информационная продукция
  '902000000000000000': 2, // Направления публикаций
  '900000000000000000': 3, // IPTC
  '925000000000000000': 4, // Макрогеографические регионы
  '901000000000000000': 5, // Страны мира и территории
  '082000000000000000': 6, // Административно-территориальные образования
  '905000000000000000': 7, // Коды РТС
  '910000000000000000': 8, // Коды СПБ
  '907000000000000000': 9, // Периодические издания
  '920000000000000000': 10, // Тип новости
} as const


type CodeWithPath = Partial<Pick<CodeWithCategory, 'path'>>

type CodeIs = Record<keyof (typeof codeTypes), (code: CodeWithPath) => boolean>


export const codeIs = Object.entries(codeTypes)
  .reduce(
    (res, [codeType, codePrefixes]) => {
      res[codeType] = (code: CodeWithPath) => codePrefixes
        .some((codePrefix) => code.path?.startsWith(codePrefix))
      return res
    },
    {} as CodeIs,
  )

export function isFeedAndPubCode(item: CodeWithCategory) {
  return (
    codeIs.publication(item)
    || codeIs.feed(item)
    || codeIs.periodical(item)
  )
}

export function isNonFeedAndPubCode(item: CodeWithCategory) {
  return !isFeedAndPubCode(item)
}

export function containsEnglish(code: CodeWithCategoryAndChildren): boolean {
  return (
    code.is_en
    || code.children.reduce((res, item) => res || containsEnglish(item), false)
  )
}

export function consistsOfEnglish(code: CodeWithCategoryAndChildren): boolean {
  return (
    code.is_en
    && code.children.reduce((res, item) => res && consistsOfEnglish(item), true)
  )
}

export function walkCodesTree(
  items: CodeWithCategoryAndChildren[],
  callback: (item: CodeWithCategoryAndChildren, level: number) => void,
  level: number = 0,
): void {
  items.forEach((item) => {
    callback(item, level)

    if (item.children) {
      walkCodesTree(item.children, callback, level + 1)
    }
  })
}

export function mapFromCodesTree(items?: CodeWithCategoryAndChildren[] | null) {
  const map: Record<number, CodeWithCategoryAndChildren> = {}
  walkCodesTree(items ?? [], (code) => map[code.id] = code)
  return map
}

// creates new objects!
export function filterCodesTree(
  items: CodeWithCategoryAndChildren[],
  includeCriteria: (item: CodeWithCategoryAndChildren) => boolean,
  firstLevelOnly: boolean, // if check code prefixes
): CodeWithCategoryAndChildren[] {
  const res: CodeWithCategoryAndChildren[] = []

  items.forEach((item) => {
    const include = includeCriteria(item)

    if (include) {
      // copy object only if it's about to include in new tree,
      // (copy without children, otherwise we'll destroy structure)
      let newItem: any = Object.assign({}, item)
      delete newItem.children
      newItem = cloneDeep(newItem)

      res.push(newItem)

      if (item.children) {
        newItem.children = filterCodesTree(
          item.children,
          firstLevelOnly ? () => true : includeCriteria,
          false,
        )
      }
    }
  })

  return res
}

export function formatCodePath(path?: string): string {
  return ((path ?? '').match(/.{1,3}/g) || []).join('.')
}

export function getSelectedIdsFromTree(
  items: CodeWithCategoryAndChildren[],
  included: number[],
  excluded: number[],
): number[] {
  const res: number[] = []

  function processItem(
    item: CodeWithCategoryAndChildren,
    inheritedState: 'included' | 'excluded' | null,
  ) {
    if (included.includes(item.id)) {
      res.push(item.id)
      inheritedState = 'included'
    } else if (excluded.includes(item.id)) {
      inheritedState = 'excluded'
    } else if (inheritedState === 'included') {
      res.push(item.id)
    }
    item.children?.forEach((item) => processItem(item, inheritedState))
  }

  items.forEach((item) => processItem(item, null))

  return res
}

/*
  function processCodeItem(item: CodeWithCategoryAndChildren, inheritState: EQuadboxState | null) {
    const explicitState = selectState.value[item.id]

    if (explicitState && isExplicitState(explicitState)) {
      inheritState = implicitFrom(explicitState)
    } else if (inheritState) {
      selectState.value[item.id] = inheritState
    }

    item.children?.forEach((item) => processCodeItem(item, inheritState))
  }
*/
